import { Check, Languages } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useCanChooseLanguage, useLanguage } from '@/hooks/useLanguage';
import { LANGUAGES, LANGUAGE_OPTIONS } from '@/i18n/languages';
import { cn } from '@/lib/utils';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@xtreed/academy-shared/ui';

interface LanguageSelectorProps {
  className?: string;
  align?: 'start' | 'center' | 'end';
}

export function LanguageSelector({
  className,
  align = 'end',
}: LanguageSelectorProps) {
  const { t } = useTranslation();
  const { language, setLanguage } = useLanguage();
  const canChoose = useCanChooseLanguage();

  if (!canChoose) return null;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          className={cn(
            'flex items-center gap-1.5 rounded-lg p-2 text-muted-foreground transition-colors hover:bg-secondary/80',
            className,
          )}
          title={t('language.select')}
          aria-label={t('language.select')}
        >
          <Languages className="h-4 w-4" />
          <span className="text-xs font-semibold">
            {LANGUAGES[language].short}
          </span>
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align={align} className="w-52">
        {LANGUAGE_OPTIONS.map((option) => (
          <DropdownMenuItem
            key={option}
            onClick={() => setLanguage(option)}
            className="flex items-center justify-between gap-2"
          >
            <span lang={LANGUAGES[option].tag}>{LANGUAGES[option].label}</span>
            {option === language && (
              <Check className="h-4 w-4 text-primary-ink" />
            )}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
